import React, { FC, ReactElement, ReactNode, useContext, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMeasure, useWindowSize } from 'react-use';
import { useLocation, useNavigate } from 'react-router-dom';
import ThemeContext from '../../contexts/themeContext';
import Button from '../../components/bootstrap/Button';
import Portal from '../Portal/Portal';
import useDarkMode from '../../hooks/useDarkMode';
import logo from '../../assets/logos/logo.png';
import logodark from '../../assets/logos/logodark.png';
import { useWizardContext } from '../../contexts/wizardContext';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '../../components/bootstrap/Modal';
import Icon from '../../components/icon/Icon';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../components/bootstrap/Dropdown';
import Popovers from '../../components/bootstrap/Popovers';
import showNotification from '../../components/extras/showNotification';
import { dashboardPagesMenu, demoPagesMenu } from '../../menu';
import AuthContext from '../../contexts/authContext';
import '../../styles/custom/customStyle.css';

interface IHeaderLeftProps {
	children: ReactNode;
	className?: string;
}
export const HeaderLeft: FC<IHeaderLeftProps> = ({ children, className }) => {
	return <div className={classNames('header-left', 'col-md', className)}>{children}</div>;
};
HeaderLeft.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
HeaderLeft.defaultProps = {
	className: undefined,
};

interface IHeaderRightProps {
	children: ReactNode;
	className?: string;
}
export const HeaderRight: FC<IHeaderRightProps> = ({ children, className }) => {
	const [ref, { height }] = useMeasure<HTMLDivElement>();

	const root = document.documentElement;
	root.style.setProperty('--header-right-height', `${height}px`);

	return (
		<div ref={ref} className={classNames('header-right', 'col-md-auto', className)}>
			{children}
		</div>
	);
};
HeaderRight.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
HeaderRight.defaultProps = {
	className: undefined,
};

interface IHeaderProps {
	children: ReactElement<IHeaderLeftProps> | ReactElement<IHeaderRightProps> | ReactNode;
	hasLeftMobileMenu?: boolean;
	hasRightMobileMenu?: boolean;
}
const Header: FC<IHeaderProps> = ({ children, hasLeftMobileMenu, hasRightMobileMenu }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { authData } = useContext(AuthContext);
	const { themeStatus, darkModeStatus } = useDarkMode();
	const wizardContext = useWizardContext();
	const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);

	const windowsWidth = useWindowSize().width;
	const [refMobileHeader, sizeMobileHeader] = useMeasure<HTMLDivElement>();
	const [refHeader, sizeHeader] = useMeasure<HTMLDivElement>();

	const root = document.documentElement;
	root.style.setProperty('--mobile-header-height', `${sizeMobileHeader.height}px`);
	root.style.setProperty('--header-height', `${sizeHeader.height}px`);

	const {
		asideStatus,
		setAsideStatus,
		toggleAsideDebounced,
		leftMenuStatus,
		setLeftMenuStatus,
		rightMenuStatus,
		setRightMenuStatus,
	} = useContext(ThemeContext);

	const isLoginPage = location.pathname === demoPagesMenu.login.path;
	const isLoggedIn = !!authData?.token?.accessToken;

	// Handler to show confirmation modal
	const handleSaveExitClick = () => {
		setShowExitConfirmModal(true);
	};

	// Handler to cancel and stay on current step
	const handleCancelExit = () => {
		setShowExitConfirmModal(false);
	};

	// Handler to confirm and execute the actual save & exit
	const handleConfirmExit = () => {
		setShowExitConfirmModal(false);
		if (wizardContext?.handleExit) {
			wizardContext.handleExit();
		}
	};

	// Handler for guideline menu items
	const handleGuidelineClick = (guidelineType: 'individual' | 'joint') => {
		if (guidelineType === 'individual') {
			// Open Individual BO Account guide in a new tab
			window.open(dashboardPagesMenu.individualNote.path, '_blank');
		} else if (guidelineType === 'joint') {
			// Open Joint BO Account guide in a new tab
			window.open(dashboardPagesMenu.jointNote.path, '_blank');
		}
	};

	// No longer adding overflow-hidden to body on mobile so page scroll works (max 767px)

	// On mobile screens, open the right menu by default so its content is visible
	useLayoutEffect(() => {
		if (windowsWidth < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) {
			setRightMenuStatus(true);
		}
	}, [windowsWidth, setRightMenuStatus]);

	return (
		<>
			<header ref={refMobileHeader} className='mobile-header'>
				<div className='container-fluid'>
					<div
						className={classNames('row align-items-center', {
							'flex-wrap': windowsWidth <= 354,
						})}>
						{windowsWidth <= 272 && isLoggedIn ? (
							<>
								{/* First line: logo only */}
								<div className='col-12 header-logo-left-mobile'>
									<img
										src={themeStatus === 'dark' ? logodark : logo}
										alt='Company'
										height={40}
									/>
								</div>
								{/* Second line: aside + right toggles */}
								<div className='col-12 d-flex justify-content-between mt-1'>
									{/* Aside toggle (only when logged in) */}
									<Button
										type='button'
										aria-label='Toggle Aside'
										className='mobile-header-toggle'
										size='lg'
										color={asideStatus ? 'primary' : themeStatus}
										isLight={asideStatus}
										icon={asideStatus ? 'FirstPage' : 'LastPage'}
										onTouchEnd={(e: React.TouchEvent) => {
											e.preventDefault();
											toggleAsideDebounced();
											setLeftMenuStatus(false);
											setRightMenuStatus(false);
										}}
										onClick={(e: React.MouseEvent) => {
											e.preventDefault();
											e.stopPropagation();
											toggleAsideDebounced();
											setLeftMenuStatus(false);
											setRightMenuStatus(false);
										}}
									/>
									{/* Right mobile menu toggle */}
									{hasRightMobileMenu && (
										<Button
											aria-label='Toggle Right Menu'
											className='mobile-header-toggle'
											size='lg'
											color={rightMenuStatus ? 'primary' : themeStatus}
											isLight={rightMenuStatus}
											icon={rightMenuStatus ? 'Menu' : 'MenuOpen'}
											onClick={() => {
												setAsideStatus(false);
												setLeftMenuStatus(false);
												setRightMenuStatus(!rightMenuStatus);
											}}
										/>
									)}
								</div>
							</>
						) : (
							<>
								<div className='col header-logo-left-mobile'>
									<img
										src={themeStatus === 'dark' ? logodark : logo}
										alt='Company'
										height={40}
									/>
									{/* Show aside toggle only when user is logged in */}
									{isLoggedIn && (
										<Button
											type='button'
											aria-label='Toggle Aside'
											className='mobile-header-toggle'
											size='lg'
											color={asideStatus ? 'primary' : themeStatus}
											isLight={asideStatus}
											icon={asideStatus ? 'FirstPage' : 'LastPage'}
											onTouchEnd={(e: React.TouchEvent) => {
												e.preventDefault();
												toggleAsideDebounced();
												setLeftMenuStatus(false);
												setRightMenuStatus(false);
											}}
											onClick={(e: React.MouseEvent) => {
												e.preventDefault();
												e.stopPropagation();
												toggleAsideDebounced();
												setLeftMenuStatus(false);
												setRightMenuStatus(false);
											}}
										/>
									)}
								</div>
								{/* Middle area: actions live in the right menu (HeaderRight) to avoid duplication */}
								<div className='col' />
								{/* Right mobile menu toggle is always available */}
								{hasRightMobileMenu && (
									<div className='col-auto'>
										<Button
											aria-label='Toggle Right Menu'
											className='mobile-header-toggle'
											size='lg'
											color={rightMenuStatus ? 'primary' : themeStatus}
											isLight={rightMenuStatus}
											icon={rightMenuStatus ? 'Menu' : 'MenuOpen'}
											onClick={() => {
												setAsideStatus(false);
												setLeftMenuStatus(false);
												setRightMenuStatus(!rightMenuStatus);
											}}
										/>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</header>
			<header
				ref={refHeader}
				className={classNames('header', {
					'header-left-open': leftMenuStatus,
					'header-right-open': rightMenuStatus,
				})}>
				<div className='container-fluid'>
					<div className='row d-flex align-items-center'>
						{children}
						{/* {(leftMenuStatus || rightMenuStatus) && (
							<Portal>
								<div
									role='presentation'
									className={classNames('header-overlay', {
										'header-overlay-left-menu': leftMenuStatus,
										'header-overlay-right-menu': rightMenuStatus,
									})}
									onClick={() => {
										setAsideStatus(false);
										setLeftMenuStatus(false);
										setRightMenuStatus(false);
									}}
								/>
							</Portal>
						)} */}
					</div>
				</div>
			</header>

			{/* Save & Exit Confirmation Modal */}
			<Modal
				isOpen={showExitConfirmModal}
				setIsOpen={setShowExitConfirmModal}
				titleId='exitConfirmModalMobile'
				isCentered
				isStaticBackdrop>
				<ModalHeader setIsOpen={setShowExitConfirmModal}>
					<ModalTitle id='exitConfirmModalMobile'>
						<Icon icon='Warning' className='me-2' color='warning' />
						Confirm Save & Exit
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<p className='lead'>
						Kindly complete all required fields before exiting to avoid losing any
						unsaved data (অনুগ্রহ করে বের হওয়ার আগে সকল প্রয়োজনীয় ঘর পূরণ করুন,
						অন্যথায় অসম্পাদিত তথ্য হারিয়ে যেতে পারে।).
					</p>
					<p className='text-muted'>Do you want to continue?</p>
				</ModalBody>
				<ModalFooter>
					<Button color='danger' onClick={handleCancelExit}>
						Cancel
					</Button>
					<Button color='success' onClick={handleConfirmExit}>
						OK
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};
Header.propTypes = {
	children: PropTypes.node.isRequired,
	hasLeftMobileMenu: PropTypes.bool,
	hasRightMobileMenu: PropTypes.bool,
};
Header.defaultProps = {
	hasLeftMobileMenu: true,
	hasRightMobileMenu: true,
};

export default Header;
